import { useCallback, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import H1 from '@base/H1/H1';
import H3 from '@base/H3/H3';
import Paper from '@base/Paper/Paper';
import Image from '@base/Image';
import IconButton from '@base/IconButton/IconButton';
import ChevronLeft from '@icon/ChevronLeft';
import ChevronRight from '@icon/ChevronRight';
import useWindowSize from '@hooks/useWindowSize';
import boxShadowGenerator from '@utils/boxShadowGenerator';

const carousel1 = 'careers/carousel1.png';
const carousel2 = 'careers/carousel2.png';
const carousel3 = 'careers/carousel3.png';
const carousel4 = 'careers/carousel4.png';
const carousel5 = 'careers/carousel5.png';

const Wrapper = styled(Paper)`
  grid-column: span 24;
  padding: ${Spacings.SPACING_30B} ${Spacings.SPACING_0B};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_15B} ${Spacings.SPACING_0B};
  }
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_15B} ${Spacings.SPACING_0B};
  }
`;
const HeadLine = styled(H1)`
  font-size: 40px;
  line-height: 120%;
  margin-bottom: ${Spacings.SPACING_2B};
  ${mediaQuery.uptoMobile} {
    line-height: 150%;
    margin-bottom: ${Spacings.SPACING_1B};
    font-size: ${Fonts.H4};
  }
`;
const Subtext = styled(H3)`
  font-weight: ${Fonts.REGULAR};
  text-align: center;
  width: 560px;
  line-height: 150%;
  margin-bottom: ${Spacings.SPACING_24B};

  ${mediaQuery.uptoMobile} {
    width: 350px;
    font-size: ${Fonts.SMALL};
    margin-bottom: ${Spacings.SPACING_10B};
  }
`;

const SlideShowImageWraper = styled.div`
  position: relative;
  width: 1134px;
  height: 516px;

  ${mediaQuery.uptoTablet} {
    width: 756px;
    height: 344px;
  }
  ${mediaQuery.uptoMobile} {
    width: 328px;
    height: 192px;
  }
`;
const SlideShowImage = styled(Image)`
  object-position: 20% 20%;
  opacity: ${({ $opacity }) => Number($opacity)};
  transition: opacity 500ms ease-in-out;
  border-radius: 8px;
`;

const ControlButton = styled(IconButton)`
  position: absolute;
  visibility: hidden;
  width: 64px;
  height: 64px;
  opacity: 0;
  transition: visibility 0s linear 300ms, opacity 300ms;
  top: calc(50% - 16px);
  box-shadow: ${boxShadowGenerator()};
  ${SlideShowImageWraper}:hover & {
    visibility: visible;
    opacity: 1;
    transition: visibility 0s linear 0s, opacity 300ms;
  }
  ${({ direction }) => (direction === 'prev' ? 'left' : 'right')}: -32px;

  ${mediaQuery.uptoMobile} {
    width: 32px;
    height: 32px;
    ${({ direction }) => (direction === 'prev' ? 'left' : 'right')}: -16px;
    visibility: visible;
    opacity: 1;
  }
`;
const MiniGallery = styled.div`
  display: flex;
  margin-top: ${Spacings.SPACING_5B};
  width: 1134px;
  justify-content: space-between;

  ${mediaQuery.uptoTablet} {
    width: 690px;
  }
  ${mediaQuery.uptoMobile} {
    margin-top: 10px;
    width: 328px;
  }
`;
const SmallImageDiv = styled.div`
  position: relative;
  height: 168px;
  width: 271px;
  background: var(--color-i-gray-2);
  border-radius: 8px;
  ${mediaQuery.uptoTablet} {
    height: 100px;
    width: 160px;
  }
  ${mediaQuery.uptoMobile} {
    width: 104px;
    height: 65px;
  }
`;
const StyledImage = styled(Image)`
  border-radius: 8px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 200ms ease-in-out, transform 200ms ease-in-out;
  &:hover {
    opacity: 1;
  }
`;
const imageUrls = [carousel1, carousel2, carousel3, carousel4, carousel5];

const SlideShow = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const { isMobile } = useWindowSize();

  const miniGallery = () => {
    const images = [];
    for (
      let index = currentImage + 1 >= imageUrls.length ? 0 : currentImage + 1,
        count = 0;
      index !== currentImage;
      index = index + 1 >= imageUrls.length ? 0 : index + 1, count += 1
    ) {
      if (isMobile && count >= 3) break;
      if (count >= 4) break;
      images.push(
        <SmallImageDiv
          key={`carousel-${index}`}
          onClick={() => setCurrentImage(index)}
        >
          <StyledImage
            src={imageUrls[index]}
            layout="fill"
            alt={`carousel ${index}`}
          />
        </SmallImageDiv>
      );
    }
    return images;
  };

  const handleNext = useCallback(() => {
    if (imageUrls.length - 1 >= currentImage) setCurrentImage(currentImage + 1);
  }, [currentImage]);

  const handlePrev = useCallback(() => {
    if (currentImage > 0) setCurrentImage(currentImage - 1);
  }, [currentImage]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (imageUrls.length - 2 >= currentImage) {
        handleNext();
      } else {
        setCurrentImage(0);
      }
    }, 4000);

    return () => {
      clearInterval(timer);
    };
  }, [currentImage, handleNext, handlePrev]);

  const controlButtons = () => {
    const prevAvailable = currentImage > 0;
    const nextAvailable = currentImage < imageUrls.length - 1;
    return (
      <>
        {prevAvailable ? (
          <ControlButton
            onClick={handlePrev}
            direction="prev"
            bgColor="var(--color-base-1)"
            hovDisabled
            $topAdjustment="18px"
          >
            <ChevronLeft
              size="24px"
              aria-hidden="true"
              color="var(--color-base-fill)"
            />
          </ControlButton>
        ) : null}
        {nextAvailable ? (
          <ControlButton
            onClick={handleNext}
            direction="next"
            bgColor="var(--color-base-1)"
            hovDisabled
            $topAdjustment="18px"
          >
            <ChevronRight
              size="24px"
              aria-hidden="true"
              color="var(--color-base-fill)"
            />
          </ControlButton>
        ) : null}
      </>
    );
  };

  return (
    <Wrapper>
      <HeadLine as="h2">Experience life at Unacademy</HeadLine>
      <Subtext>
        We’re building a workspace that promotes growth, both personally and
        professionally.
      </Subtext>
      <SlideShowImageWraper>
        {imageUrls.map((image, index) => (
          <SlideShowImage
            src={image}
            $opacity={image === imageUrls[currentImage]}
            layout="fill"
            objectFit="cover"
            // eslint-disable-next-line react/no-array-index-key
            key={`image-slideshow-${index}`}
          />
        ))}

        {controlButtons()}
      </SlideShowImageWraper>
      <MiniGallery>{miniGallery()}</MiniGallery>
    </Wrapper>
  );
};
export default SlideShow;
